// StaysEventsMaintenanceTab — pestaña superadmin para operar la cola
// del cron de Stays.
//
// Round 2026-05-29 fase 4. Nació de la incidencia "Carolina/VISTAMAR":
// un booking real quedó `done` en stays_events pero la reserva no se
// creó por un block fantasma. Esta UI permite buscar/re-encolar eventos
// sin entrar a la DB con psql.
//
// Soporta:
//   - Filtrar por entity_key, action_type, status.
//   - Ver el último error completo (drawer) para los `failed`.
//   - Botón "Reintentar" por fila (cambia status → queued + attempts=0).
//   - Selección masiva + "Reintentar seleccionados".
//
// El backend rechaza el reset cuando el evento está `processing`
// (para no pisarse con el cron). El cron toma los queued con
// next_attempt_at <= NOW() en su próximo tick (~1 min).

import { useCallback, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Drawer,
  Form,
  Input,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  SearchOutlined,
  ReloadOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { apiFetch } from "../../services/apiClient";
import { getModal, getMsg } from "../../utils/antdStatic";
import { paginacionCliente } from "../../utils/tablaPaginacion";

const { Text, Paragraph } = Typography;

type StaysEventStatus = "queued" | "processing" | "done" | "failed";
type StaysActionType = "reservation" | "payment";

interface EventRow {
  id: number;
  entityKey: string;
  actionType: StaysActionType;
  lastDt: string;
  status: StaysEventStatus;
  attempts: number;
  nextAttemptAt: string;
  lastError: string | null;
  createdAt: string;
  updatedAt: string;
}

interface SearchResponse {
  rows: EventRow[];
  scanned: number;
  truncated: boolean;
}

interface RetryResponse {
  id: number;
  entityKey: string;
  actionType: StaysActionType;
  previousStatus: StaysEventStatus;
  newStatus: "queued";
}

interface RetryBulkResponse {
  reset: number;
  skipped: Array<{ id: number; reason: string }>;
}

const STATUS_META: Record<
  StaysEventStatus,
  { label: string; color: string }
> = {
  queued: { label: "En cola", color: "blue" },
  processing: { label: "Procesando", color: "geekblue" },
  done: { label: "Procesado", color: "green" },
  failed: { label: "Falló", color: "red" },
};

const StaysEventsMaintenanceTab = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [retryingId, setRetryingId] = useState<number | null>(null);
  const [bulkRetrying, setBulkRetrying] = useState(false);
  const [rows, setRows] = useState<EventRow[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [detailRow, setDetailRow] = useState<EventRow | null>(null);

  const runSearch = useCallback(async () => {
    setLoading(true);
    setSelectedIds([]);
    try {
      const values = await form.validateFields();
      const qs = new URLSearchParams();
      if (values.entity_key) qs.set("entity_key", values.entity_key.trim());
      if (values.action_type) qs.set("action_type", values.action_type);
      if (values.status) qs.set("status", values.status);
      qs.set("limit", "200");
      const res = await apiFetch<{ success: boolean; data: SearchResponse }>(
        `/admin/stays-events/search?${qs.toString()}`,
      );
      if (res?.data) {
        setRows(res.data.rows);
        setTruncated(res.data.truncated);
      }
    } finally {
      setLoading(false);
    }
  }, [form]);

  const doRetry = useCallback(
    async (id: number) => {
      setRetryingId(id);
      try {
        const res = await apiFetch<{ success: boolean; data: RetryResponse }>(
          "/admin/stays-events/retry",
          {
            method: "POST",
            body: JSON.stringify({ id }),
          },
        );
        if (res?.data) {
          getMsg()?.success(
            `Event ${id} re-encolado (${res.data.previousStatus} → queued). ` +
              `El cron lo procesa en ~1 min.`,
          );
          await runSearch();
        }
      } finally {
        setRetryingId(null);
      }
    },
    [runSearch],
  );

  const confirmRetry = useCallback(
    (row: EventRow) => {
      getModal().confirm({
        title: `¿Re-encolar evento ${row.id}?`,
        content: (
          <div>
            <Paragraph>
              <Text code>{row.entityKey}</Text> ({row.actionType}) pasará de{" "}
              <Tag color={STATUS_META[row.status].color}>
                {STATUS_META[row.status].label}
              </Tag>{" "}
              a <Tag color="blue">En cola</Tag>. El cron lo re-procesa en su
              próximo tick (~1 min). Se resetea <Text code>attempts=0</Text>{" "}
              y se limpia el último error.
            </Paragraph>
            <Paragraph type="secondary" style={{ marginBottom: 0 }}>
              No se modifica el payload original — el handler de Stays va a
              reusar el mismo snapshot. Si la causa del fallo no se arregló,
              el evento volverá a fallar.
            </Paragraph>
          </div>
        ),
        okText: "Re-encolar",
        cancelText: "Cancelar",
        onOk: () => doRetry(row.id),
      });
    },
    [doRetry],
  );

  const confirmRetryBulk = useCallback(() => {
    if (selectedIds.length === 0) return;
    getModal().confirm({
      title: `¿Re-encolar ${selectedIds.length} eventos?`,
      content: (
        <Paragraph>
          Todos los eventos seleccionados pasarán a <Tag color="blue">En cola</Tag>{" "}
          y serán re-procesados por el cron. Los que estén en{" "}
          <Tag color="geekblue">Procesando</Tag> serán salteados.
        </Paragraph>
      ),
      okText: "Re-encolar todos",
      cancelText: "Cancelar",
      onOk: async () => {
        setBulkRetrying(true);
        try {
          const res = await apiFetch<{
            success: boolean;
            data: RetryBulkResponse;
          }>("/admin/stays-events/retry-bulk", {
            method: "POST",
            body: JSON.stringify({ ids: selectedIds }),
          });
          if (res?.data) {
            const skipNote =
              res.data.skipped.length > 0
                ? ` (${res.data.skipped.length} salteados)`
                : "";
            getMsg()?.success(
              `${res.data.reset} eventos re-encolados${skipNote}.`,
            );
            await runSearch();
          }
        } finally {
          setBulkRetrying(false);
        }
      },
    });
  }, [selectedIds, runSearch]);

  const columns: ColumnsType<EventRow> = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 80,
    },
    {
      title: "Entity key",
      dataIndex: "entityKey",
      key: "entityKey",
      width: 220,
      render: (v: string) => (
        <Text code copyable={{ text: v }}>
          {v.length > 20 ? `${v.slice(0, 18)}…` : v}
        </Text>
      ),
    },
    {
      title: "Tipo",
      dataIndex: "actionType",
      key: "actionType",
      width: 110,
      render: (v: StaysActionType) => (
        <Tag color={v === "reservation" ? "blue" : "purple"}>{v}</Tag>
      ),
    },
    {
      title: "Estado",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (v: StaysEventStatus) => {
        const meta = STATUS_META[v];
        return <Tag color={meta.color}>{meta.label}</Tag>;
      },
    },
    {
      title: "Intentos",
      dataIndex: "attempts",
      key: "attempts",
      width: 90,
    },
    {
      title: "Último update",
      dataIndex: "updatedAt",
      key: "updatedAt",
      width: 160,
      render: (v: string) => new Date(v).toLocaleString("es-CL"),
    },
    {
      title: "Acciones",
      key: "actions",
      width: 180,
      fixed: "right",
      render: (_, row) => (
        <Space size="small">
          <Button
            size="small"
            icon={<EyeOutlined />}
            onClick={() => setDetailRow(row)}
          >
            Ver
          </Button>
          <Button
            size="small"
            type="primary"
            ghost
            icon={<ReloadOutlined />}
            disabled={row.status === "processing"}
            loading={retryingId === row.id}
            onClick={() => confirmRetry(row)}
          >
            Reintentar
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      <Alert
        type="info"
        showIcon
        message="Mantenimiento de la cola de Stays"
        description={
          <div>
            Permite buscar eventos en <Text code>stays_events</Text> y
            re-encolarlos manualmente para que el cron los re-procese. Útil
            cuando: (a) un evento quedó <Text code>failed</Text> por un bug
            que se arregló, o (b) un evento quedó <Text code>done</Text>{" "}
            pero la lógica falló silenciosamente y la reserva nunca se creó
            (caso "Carolina / VISTAMAR" del round 2026-05-29).
          </div>
        }
        style={{ marginBottom: 16 }}
      />

      <Card size="small" style={{ marginBottom: 16 }}>
        <Form form={form} layout="inline" onFinish={runSearch}>
          <Form.Item name="entity_key" label="Entity key">
            <Input
              placeholder="KN08J o 24-char hex"
              style={{ width: 220 }}
              allowClear
            />
          </Form.Item>
          <Form.Item name="action_type" label="Tipo">
            <Select
              style={{ width: 140 }}
              allowClear
              options={[
                { value: "reservation", label: "reservation" },
                { value: "payment", label: "payment" },
              ]}
            />
          </Form.Item>
          <Form.Item name="status" label="Estado">
            <Select
              style={{ width: 140 }}
              allowClear
              options={[
                { value: "queued", label: "En cola" },
                { value: "processing", label: "Procesando" },
                { value: "done", label: "Procesado" },
                { value: "failed", label: "Falló" },
              ]}
            />
          </Form.Item>
          <Form.Item>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              htmlType="submit"
              loading={loading}
            >
              Buscar
            </Button>
          </Form.Item>
          {selectedIds.length > 0 && (
            <Form.Item>
              <Button
                type="primary"
                danger
                icon={<ReloadOutlined />}
                loading={bulkRetrying}
                onClick={confirmRetryBulk}
              >
                Reintentar {selectedIds.length} seleccionados
              </Button>
            </Form.Item>
          )}
        </Form>
      </Card>

      {truncated && (
        <Alert
          type="warning"
          showIcon
          message="Resultados truncados a 200 filas. Refina los filtros para ver más."
          style={{ marginBottom: 16 }}
        />
      )}

      <Table<EventRow>
        rowKey="id"
        size="middle"
        columns={columns}
        dataSource={rows}
        loading={loading}
        scroll={{ x: 1000 }}
        rowSelection={{
          selectedRowKeys: selectedIds,
          onChange: (keys) => setSelectedIds(keys.map((k) => Number(k))),
          getCheckboxProps: (record) => ({
            disabled: record.status === "processing",
          }),
        }}
        pagination={paginacionCliente({ porDefecto: 25, etiqueta: "evento" })}
      />

      <Drawer
        title={detailRow ? `Event ${detailRow.id} — ${detailRow.entityKey}` : "Event"}
        width={620}
        open={detailRow !== null}
        onClose={() => setDetailRow(null)}
        destroyOnClose
      >
        {detailRow && (
          <div>
            <div style={{ marginBottom: 12 }}>
              <Tag color={STATUS_META[detailRow.status].color}>
                {STATUS_META[detailRow.status].label}
              </Tag>
              <Tag color={detailRow.actionType === "reservation" ? "blue" : "purple"}>
                {detailRow.actionType}
              </Tag>
              <Text type="secondary">attempts={detailRow.attempts}</Text>
            </div>
            <div style={{ marginBottom: 12 }}>
              <Text type="secondary">last_dt: </Text>
              <Text>{detailRow.lastDt}</Text>
            </div>
            <div style={{ marginBottom: 12 }}>
              <Text type="secondary">updated_at: </Text>
              <Text>{detailRow.updatedAt}</Text>
            </div>
            {detailRow.lastError && (
              <>
                <Text type="secondary">last_error:</Text>
                <pre
                  style={{
                    background: "#0d1117",
                    color: "#e6edf3",
                    padding: 12,
                    borderRadius: 6,
                    fontSize: 12,
                    overflow: "auto",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    marginTop: 4,
                  }}
                >
                  {detailRow.lastError}
                </pre>
              </>
            )}
            <div style={{ marginTop: 16 }}>
              <Button
                type="primary"
                icon={<ReloadOutlined />}
                disabled={detailRow.status === "processing"}
                onClick={() => {
                  setDetailRow(null);
                  confirmRetry(detailRow);
                }}
              >
                Reintentar este evento
              </Button>
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default StaysEventsMaintenanceTab;
